import { useState, useEffect, useRef } from 'react';
import { useScore } from '../../../context/ScoreContext';

const PARTICLE_COUNT = 32;

interface Particle {
  id: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
}

const COLORS = ['#d4a843', '#f5d080', '#e05555', '#ff8888', '#ffffff', '#fffbe6', '#7ec8a0', '#ffd700'];

const EFFECT_SHOWN_KEY = 'gameStartEffect_shownAt';

function generateParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    angle: (i / PARTICLE_COUNT) * 360 + Math.random() * 20 - 10,
    distance: 120 + Math.random() * 200,
    size: 4 + Math.random() * 8,
    color: COLORS[i % COLORS.length],
    delay: Math.random() * 0.4,
    duration: 0.8 + Math.random() * 0.6,
  }));
}

export default function GameStartEffect() {
  const { gameStartedAt, isLoading } = useScore();
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState<'in' | 'show' | 'out'>('in');
  const [particles] = useState<Particle[]>(() => generateParticles());
  const prevRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const dismiss = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase('out');
    const t = setTimeout(() => { setVisible(false); setPhase('in'); }, 800);
    timersRef.current = [t];
  };

  useEffect(() => {
    if (isLoading) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      prevRef.current = gameStartedAt;
      return;
    }

    if (prevRef.current === null && gameStartedAt !== null) {
      // 檢查這個 gameStartedAt 是否已經顯示過
      const shownAt = localStorage.getItem(EFFECT_SHOWN_KEY);
      if (shownAt === String(gameStartedAt)) {
        prevRef.current = gameStartedAt;
        return;
      }
      // 記錄已顯示
      localStorage.setItem(EFFECT_SHOWN_KEY, String(gameStartedAt));

      setVisible(true);
      setPhase('in');
      const t1 = setTimeout(() => setPhase('show'), 400);
      const t2 = setTimeout(() => setPhase('out'), 3200);
      const t3 = setTimeout(() => { setVisible(false); setPhase('in'); }, 4000);
      timersRef.current = [t1, t2, t3];
      return () => { timersRef.current.forEach(clearTimeout); };
    }

    prevRef.current = gameStartedAt;
  }, [gameStartedAt, isLoading]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden cursor-pointer select-none"
      onClick={dismiss}
      style={{
        background: 'radial-gradient(ellipse at center, rgba(20,15,5,0.97) 0%, rgba(5,5,5,0.98) 100%)',
        opacity: phase === 'out' ? 0 : 1,
        transition: phase === 'out' ? 'opacity 0.8s ease-out' : phase === 'in' ? 'opacity 0.3s ease-in' : 'none',
      }}
    >
      {/* Background radial rings */}
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${i * 180}px`,
            height: `${i * 180}px`,
            border: `1px solid rgba(212,168,67,${0.25 - i * 0.05})`,
            animation: `ring-expand 2s ease-out ${i * 0.12}s both`,
          }}
        />
      ))}

      {/* Gold burst rays */}
      {Array.from({ length: 12 }, (_, i) => (
        <div
          key={i}
          className="absolute origin-center"
          style={{
            width: '2px',
            height: '0px',
            background: `linear-gradient(to top, rgba(212,168,67,0.9), transparent)`,
            transform: `rotate(${i * 30}deg)`,
            transformOrigin: 'bottom center',
            bottom: '50%',
            left: 'calc(50% - 1px)',
            animation: `ray-shoot 1.2s ease-out 0.1s both`,
          }}
        />
      ))}

      {/* Particles */}
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: `particle-fly-${p.id % 4} ${p.duration}s ease-out ${p.delay}s both`,
            '--px': `${Math.cos((p.angle * Math.PI) / 180) * p.distance}px`,
            '--py': `${Math.sin((p.angle * Math.PI) / 180) * p.distance}px`,
          } as React.CSSProperties}
        />
      ))}

      {/* Central glow orb */}
      <div
        className="absolute rounded-full"
        style={{
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(212,168,67,0.4) 0%, rgba(212,168,67,0.1) 40%, transparent 70%)',
          filter: 'blur(20px)',
          animation: 'orb-pulse 0.5s ease-out 0.1s both',
        }}
      />

      {/* Main text */}
      <div className="relative flex flex-col items-center gap-2 z-10">
        <div
          className="relative"
          style={{ animation: 'text-slam 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both' }}
        >
          <p
            className="font-black text-center tracking-[0.2em] uppercase"
            style={{
              fontSize: 'clamp(36px, 8vw, 72px)',
              color: '#d4a843',
              textShadow: '0 0 20px rgba(212,168,67,0.8), 0 0 60px rgba(212,168,67,0.4), 0 0 120px rgba(212,168,67,0.2)',
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.25em',
            }}
          >
            GAME
          </p>
          <p
            className="font-black text-center uppercase"
            style={{
              fontSize: 'clamp(44px, 10vw, 90px)',
              color: '#ffffff',
              textShadow: '0 0 30px rgba(255,255,255,0.6), 0 0 80px rgba(212,168,67,0.4)',
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.15em',
              marginTop: '-0.15em',
            }}
          >
            START
          </p>
        </div>

        {/* Chinese subtitle */}
        <div style={{ animation: 'fade-up 0.5s ease-out 0.5s both' }}>
          <p
            className="text-center font-bold tracking-[0.3em]"
            style={{
              fontSize: 'clamp(14px, 3vw, 22px)',
              color: 'rgba(212,168,67,0.85)',
              textShadow: '0 0 10px rgba(212,168,67,0.5)',
            }}
          >
            ＊ 遊 戲 開 始 ＊
          </p>
        </div>

        {/* Divider line */}
        <div
          className="flex items-center gap-3"
          style={{ animation: 'fade-up 0.5s ease-out 0.7s both' }}
        >
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#d4a843]/50" />
          <i className="ri-sword-fill text-[#d4a843]/60 text-base" />
          <i className="ri-crown-fill text-[#d4a843] text-lg" style={{ textShadow: '0 0 12px rgba(212,168,67,0.8)' }} />
          <i className="ri-sword-fill text-[#d4a843]/60 text-base" style={{ transform: 'scaleX(-1)' }} />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#d4a843]/50" />
        </div>

        {/* Tap to skip hint */}
        <div style={{ animation: 'fade-up 0.5s ease-out 1.2s both' }}>
          <p
            className="text-center tracking-widest uppercase"
            style={{ fontSize: '10px', color: 'rgba(212,168,67,0.3)', fontFamily: "'Cinzel', serif", marginTop: '8px' }}
          >
            點擊畫面繼續
          </p>
        </div>
      </div>

      {/* Corner sparks */}
      {[
        { corner: 'top-8 left-8', delay: '0.6s' },
        { corner: 'top-8 right-8', delay: '0.7s' },
        { corner: 'bottom-8 left-8', delay: '0.8s' },
        { corner: 'bottom-8 right-8', delay: '0.9s' },
      ].map(({ corner, delay }) => (
        <div
          key={corner}
          className={`absolute ${corner}`}
          style={{ animation: `corner-spark 1s ease-out ${delay} both` }}
        >
          <i className="ri-flashlight-fill text-2xl text-[#d4a843]" style={{ textShadow: '0 0 12px rgba(212,168,67,0.9)' }} />
        </div>
      ))}

      <style>{`
        @keyframes ring-expand {
          from { transform: scale(0); opacity: 0.8; }
          to { transform: scale(3); opacity: 0; }
        }
        @keyframes ray-shoot {
          from { height: 0; opacity: 1; }
          50% { height: 220px; opacity: 0.9; }
          to { height: 320px; opacity: 0; }
        }
        @keyframes orb-pulse {
          from { transform: scale(0); opacity: 0; }
          50% { transform: scale(2); opacity: 1; }
          to { transform: scale(1.2); opacity: 0.6; }
        }
        @keyframes text-slam {
          from { transform: scale(3) translateY(-20px); opacity: 0; filter: blur(8px); }
          to { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes fade-up {
          from { transform: translateY(16px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes corner-spark {
          from { transform: scale(0) rotate(-45deg); opacity: 0; }
          40% { transform: scale(1.4) rotate(10deg); opacity: 1; }
          to { transform: scale(1) rotate(0deg); opacity: 0.8; }
        }
        ${particles.map(p => `
          @keyframes particle-fly-${p.id % 4} {
            from { transform: translate(-50%, -50%) scale(0); opacity: 1; }
            60% { opacity: 0.9; }
            to { transform: translate(calc(-50% + var(--px)), calc(-50% + var(--py))) scale(0.3); opacity: 0; }
          }
        `).join('')}
      `}</style>
    </div>
  );
}
